import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { contactDetailsSelectors } from 'store/selectors/contactDetails';

import { setSnackbarSuccess } from 'store/actions/global';
import { colors } from 'utils/constants';
import { showFirstErrorMessage } from 'utils/helpers';

const DialogErrorText = styled(DialogContentText)`
  color: ${colors.warning};
`;

/**
 * Save/Cancel form dialog for contact-related edits, with inline error
 * display. Its Save button branches on mode flags: submit an embedded
 * `.location-form`, update a contact's email or phone number, run an officials
 * update, or rename a contact list. Used by contact details and list detail
 * screens; the form itself is passed as `children`.
 */
const DialogUser = ({
  children,
  contactId,
  description,
  email,
  handleDataUpdate,
  handleDialogClose,
  isDialogOpen,
  isLocationDialog,
  isOfficialsDialog,
  listId,
  listName,
  phoneNumber,
  subtitle,
  title,
}) => {
  const { isAddingLocation } = useSelector(state => ({
    isAddingLocation: contactDetailsSelectors.isAddingLocation(state),
  }));
  const dispatch = useDispatch();

  const [dialogError, setDialogError] = useState('');

  const handleDialogSave = () => {
    if (contactId) {
      if (isLocationDialog) {
        document.querySelector('.location-form').dispatchEvent(
          new Event('submit', {
            bubbles: true,
            cancelable: true,
          })
        );
      } else {
        const params = email ? { email } : { phone_number: phoneNumber };

        handleDataUpdate(listId, contactId, params)
          .then(() => {
            setDialogError(false);
            handleDialogClose();
            dispatch(setSnackbarSuccess());
          })
          .catch(error => {
            setDialogError(showFirstErrorMessage(error));
          });
      }
    } else if (isOfficialsDialog) {
      handleDataUpdate()
        .then(() => {
          setDialogError(false);
          handleDialogClose();
        })
        .catch(() => setDialogError(true));
    } else {
      handleDataUpdate(listId, { name: listName })
        .then(() => {
          setDialogError(false);
          handleDialogClose();
        })
        .catch(() => setDialogError(true));
    }
  };

  return (
    <Dialog
      aria-labelledby="form-dialog-title"
      onClose={handleDialogClose}
      fullWidth
      open={isDialogOpen}
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{subtitle}</DialogContentText>
        {dialogError && <DialogErrorText>{dialogError}</DialogErrorText>}
        {children}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleDialogClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isAddingLocation || (isOfficialsDialog && !description)}
          onClick={handleDialogSave}
          type={isLocationDialog ? 'submit' : 'button'}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DialogUser.defaultProps = {
  contactId: '',
  description: '',
  email: '',
  handleDataUpdate: () => {},
  isLocationDialog: false,
  isOfficialsDialog: false,
  listId: '',
  listName: '',
  phoneNumber: '',
  subtitle: '',
};

DialogUser.propTypes = {
  children: PropTypes.element.isRequired,
  /** When set, Save updates this contact's email/phone (or submits the location form). */
  contactId: PropTypes.string,
  /** Officials-dialog value; Save is disabled while it is empty. */
  description: PropTypes.string,
  /** New email to save for the contact; takes precedence over `phoneNumber`. */
  email: PropTypes.string,
  /** Promise-returning save callback; its arguments depend on the dialog mode. */
  handleDataUpdate: PropTypes.func,
  handleDialogClose: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  /** Save dispatches a submit event on the `.location-form` element rendered in `children`. */
  isLocationDialog: PropTypes.bool,
  /** Save calls `handleDataUpdate()` with no arguments and requires `description`. */
  isOfficialsDialog: PropTypes.bool,
  listId: PropTypes.string,
  /** New name for the contact list; used when no `contactId` is given and no mode flag is set. */
  listName: PropTypes.string,
  /** New phone number to save for the contact when `email` is not provided. */
  phoneNumber: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default DialogUser;
